/**
 * bots.ts — Bot AI for practice mode
 *
 * Generates mathematical function strings for bot soldiers.
 * Two difficulty levels: Easy (random pool) and Medium (aimed parabola with noise).
 */

import { computeTrajectory, type FieldBounds, DEFAULT_FIELD_BOUNDS } from './trajectory';
import { checkObstacleCollision, type Obstacle } from './obstacles';

export enum BotDifficulty {
  Medium = 'medium',
  High = 'high',
  Impossible = 'impossible',
  OneShot = 'oneshot',
}

/** Random number in [min, max] */
function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/** Round to N decimal places */
function round(n: number, decimals: number = 2): number {
  const factor = Math.pow(10, decimals);
  return Math.round(n * factor) / factor;
}

/**
 * Simulates a trajectory to see if it hits an obstacle.
 * Returns true if it hits an obstacle BEFORE reaching the target.
 */
function simulatesCollision(
  a: number,
  b: number,
  botX: number,
  botY: number,
  targetX: number,
  direction: 1 | -1,
  obstacles: Obstacle[],
  bounds: FieldBounds
): boolean {
  if (obstacles.length === 0) return false;

  // We only care about collisions between the bot and the target
  const fn = (x: number) => a * x * x + b * x;
  const trajectory = computeTrajectory(fn, botX, botY, direction, bounds, 0.2); // coarse step for simulation speed

  // Only consider trajectory points before or at the target X
  const relevantTrajectory = trajectory.filter(p => {
    if (direction === 1) return p.x <= targetX + 1; // allow a little past target
    return p.x >= targetX - 1;
  });

  const hit = checkObstacleCollision(relevantTrajectory, obstacles, botX, botY);
  return hit !== null;
}

/**
 * Formats `a` and `b` into a math string.
 */
function formatFunction(a: number, b: number): string {
  const parts: string[] = [];
  if (a !== 0) parts.push(`${a}*x^2`);
  if (b !== 0) parts.push(`${b}*x`);
  if (parts.length === 0) return '0';
  return parts.join(' + ').replace(/\+ -/g, '- ');
}

/**
 * Medium bot: constructs a parabola aimed roughly at the nearest enemy,
 * with random noise added so it's not perfect.
 * (Not obstacle aware)
 */
function generateMediumFunction(
  botX: number,
  botY: number,
  targetX: number,
  targetY: number,
  direction: 1 | -1,
): string {
  const dx = (targetX - botX) * direction;
  const dy = targetY - botY;

  if (Math.abs(dx) < 0.5) return '10*x';

  const noise = rand(-0.35, 0.35); // accuracy noise
  const a = round(rand(-0.02, 0.02), 4);
  const b = round((dy - a * dx * dx) / dx + noise, 3);

  return formatFunction(a, b);
}

/**
 * High (Hard) bot: Complicated aiming with moderate noise (+/- 0.15).
 * Simulates shots to avoid obstacles by trying higher arcs.
 */
function generateHighFunction(
  botX: number,
  botY: number,
  targetX: number,
  targetY: number,
  direction: 1 | -1,
  obstacles: Obstacle[],
  bounds: FieldBounds
): string {
  const dx = (targetX - botX) * direction;
  const dy = targetY - botY;

  if (Math.abs(dx) < 0.5) return '10*x';

  let bestA = 0;
  let bestB = 0;
  let attempts = 0;
  let minA = -0.015;
  let maxA = 0.015;

  while (attempts < 15) {
    const noise = rand(-0.15, 0.15);
    const a = round(rand(minA, maxA), 4);
    const b = round((dy - a * dx * dx) / dx + noise, 3);

    // Save as fallback
    bestA = a;
    bestB = b;

    if (!simulatesCollision(a, b, botX, botY, targetX, direction, obstacles, bounds)) {
      break; // Found a clear path
    }

    // Hit an obstacle. Try a higher arc (more negative 'a' for upside-down parabola).
    minA -= 0.02;
    attempts++;
  }

  return formatFunction(bestA, bestB);
}

/**
 * Impossible bot: Very high precision with slight margin for error (+/- 0.03).
 * Simulates shots to avoid obstacles by trying higher arcs.
 */
function generateImpossibleFunction(
  botX: number,
  botY: number,
  targetX: number,
  targetY: number,
  direction: 1 | -1,
  obstacles: Obstacle[],
  bounds: FieldBounds
): string {
  const dx = (targetX - botX) * direction;
  const dy = targetY - botY;

  if (Math.abs(dx) < 0.1) return '100*x';

  let bestA = 0;
  let bestB = 0;
  let attempts = 0;
  let minA = -0.01;
  let maxA = 0.01;

  while (attempts < 20) {
    const noise = rand(-0.03, 0.03);
    const a = round(rand(minA, maxA), 4);
    const b = round((dy - a * dx * dx) / dx + noise, 4);

    bestA = a;
    bestB = b;

    if (!simulatesCollision(a, b, botX, botY, targetX, direction, obstacles, bounds)) {
      break; // Clear path!
    }

    // Try progressively higher arcs
    minA -= 0.03;
    attempts++;
  }

  return formatFunction(bestA, bestB);
}

/**
 * Generates a function string for a bot.
 */
export function generateBotFunction(
  difficulty: BotDifficulty,
  botX: number,
  botY: number,
  targetX: number,
  targetY: number,
  direction: 1 | -1,
  obstacles: Obstacle[] = [],
  bounds: FieldBounds = DEFAULT_FIELD_BOUNDS
): string {
  switch (difficulty) {
    case BotDifficulty.Medium:
      return generateMediumFunction(botX, botY, targetX, targetY, direction);
    case BotDifficulty.High:
      return generateHighFunction(botX, botY, targetX, targetY, direction, obstacles, bounds);
    case BotDifficulty.Impossible:
    case BotDifficulty.OneShot:
      return generateImpossibleFunction(botX, botY, targetX, targetY, direction, obstacles, bounds);
    default:
      return generateMediumFunction(botX, botY, targetX, targetY, direction);
  }
}
